/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEvOverviewData } from '@apps/context/EvOverviewContext';
import { Table } from '@crudx/mui';
import { CircularProgress, debounce } from '@mui/material';

import { useColumns } from './columns';
import { CarType, Props } from './props';
import { StyledWrapper } from './styled';

const END_REACHED_THRESOLD = 0.05;

const EvOverviewContainer: React.FC<Props> = ({ evOverview, webView }) => {
  // ================== HOKS
  const { fetchMore, isFetching, isLoading } = useEvOverviewData();
  const [isMounted, setIsMounted] = useState(false);
  const tableRef = useRef<any>();
  const [result, setResult] = useState<CarType[] | undefined>(evOverview?.data);
  const [page, setPage] = useState<number>(1);

  // ================== VARIABLES
  const lastPage = evOverview?.last_page ?? 1;
  const hasMorePage = page !== lastPage && isMounted && !isLoading;

  // ================== EVENTS
  const onReset = () => {
    setPage(1);
    const tableContainer = tableRef.current;
    tableContainer.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const onChange = (values?: CarType[]) => {
    setResult(values);
  };

  const columns = useColumns({ onChange, onReset, webView });

  const onFetchMore = useCallback(async () => {
    if (isFetching || isLoading || !hasMorePage) return;
    await fetchMore?.({
      page: page + 1,
      onCompleted: ({ data, current_page }) => {
        setPage(current_page);
        setResult((prev) => {
          return [...(prev || []), ...(data || [])];
        });
      },
    });
  }, [isFetching, hasMorePage, isLoading, page]);

  // ================== EFFECTS
  useEffect(() => {
    const tableContainer = tableRef.current;
    const scrollListener = debounce(() => {
      const tableHeight = tableContainer?.clientHeight ?? 0;
      const scrollTop = tableContainer?.scrollTop ?? 0;
      const scrollHeight = tableContainer?.scrollHeight ?? 0;
      const bottom = scrollHeight - tableHeight;
      const threshold = bottom * END_REACHED_THRESOLD;
      const calcBottom = bottom - threshold;
      const isEndReached = scrollTop > calcBottom;

      if (isEndReached) {
        onFetchMore();
      }
    }, 250);

    tableContainer.addEventListener('scroll', scrollListener);
    return () => {
      tableContainer.removeEventListener('scroll', scrollListener);
    };
  }, [onFetchMore]);

  useEffect(() => {
    // ============ TO AVOID HYDRATION ERROR FOR FOOTER VIEW
    setIsMounted(true);
  }, []);

  // ================== VIEWS
  return (
    <StyledWrapper>
      <Table<CarType>
        data={result}
        columns={columns}
        stickyHeader={true}
        size="small"
        page={1}
        total={100}
        pageSize={10}
        striped={true}
        tableHeadBorderTop={false}
        pagination={false}
        tableContainerProps={{
          sx: {
            overscrollBehavior: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '-ms-overflow-style': 'none', // scrollbar in IE and Edge
            'scrollbar-width': 'none', // scrollbar in Firefox
          },
          ref: tableRef,
        }}
        {...(hasMorePage && {
          tableFooterProps: {
            sx: {
              position: 'absolute',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 5,
            },
          },
          footerView: (
            <CircularProgress size={26} sx={{ color: 'secondary.main' }} />
          ),
        })}
      />
    </StyledWrapper>
  );
};

export default EvOverviewContainer;
